import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../Config';

const ListeReparationsVehicule = () => {
    const [reparations, setReparations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredReparations, setFilteredReparations] = useState([]);
    const [stats, setStats] = useState({
        total: 0,
        totalMontant: 0,
        moyenne: 0
    });

    const token = localStorage.getItem('token');
    const navigate = useNavigate();

    useEffect(() => {
        fetchReparations();
    }, []);

    useEffect(() => {
        if (searchTerm === '') {
            setFilteredReparations(reparations);
        } else {
            const filtered = reparations.filter(reparation => {
                const searchLower = searchTerm.toLowerCase();
                const immatriculation = reparation.vehicule_info?.immatriculation || '';
                const marque = reparation.vehicule_info?.marque || '';
                const modele = reparation.vehicule_info?.modele || '';
                const demandeurNom = reparation.demandeur_info?.nom || '';
                const mecanicienNom = reparation.mecanicien?.nom || '';
                
                return (
                    reparation.id.toString().includes(searchLower) ||
                    immatriculation.toLowerCase().includes(searchLower) ||
                    marque.toLowerCase().includes(searchLower) ||
                    modele.toLowerCase().includes(searchLower) ||
                    demandeurNom.toLowerCase().includes(searchLower) ||
                    mecanicienNom.toLowerCase().includes(searchLower) ||
                    (reparation.description_finale && 
                     reparation.description_finale.toLowerCase().includes(searchLower))
                );
            });
            setFilteredReparations(filtered);
        }
    }, [searchTerm, reparations]);

    const fetchReparations = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_URL}/reparations`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            setReparations(response.data);
            setFilteredReparations(response.data);
            calculateStats(response.data);
            setError(null);
        } catch (err) {
            console.error('Erreur détaillée:', err.response?.data || err.message);
            setError('Erreur lors du chargement des réparations: ' + (err.response?.data?.message || err.message));
        } finally {
            setLoading(false);
        }
    };

    const calculateStats = (data) => {
        const total = data.length;
        const totalMontant = data.reduce((sum, rep) => sum + parseFloat(rep.montant || 0), 0);
        const moyenne = total > 0 ? totalMontant / total : 0;
        
        setStats({
            total,
            totalMontant: totalMontant.toFixed(2),
            moyenne: moyenne.toFixed(2)
        });
    };

    const handleViewDetails = (id) => {
        navigate(`/reparations/${id}`);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const formatCurrency = (amount) => {
        return parseFloat(amount || 0).toFixed(2) + ' MAD';
    };

    if (loading) {
        return (
            <div className="min-h-screen  py-8">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="text-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-700 mx-auto"></div>
                        <p className="mt-4 text-gray-600">Chargement des réparations...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen  py-8">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="bg-white border border-gray-300 rounded-lg p-6">
                        <h2 className="text-xl font-semibold text-red-700 mb-2">Erreur</h2>
                        <p className="text-gray-600 mb-4">{error}</p>
                        <button
                            onClick={fetchReparations}
                            className="bg-gray-800 text-white px-4 py-2 rounded hover:bg-gray-700 transition text-sm"
                        >
                            Réessayer
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen  py-8">
            <div className="max-w-7xl mx-auto px-4">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex justify-between items-center mb-6">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Liste des réparations</h1>
                            <p className="text-gray-600 mt-1">
                                {stats.total} réparation(s) • Total: {formatCurrency(stats.totalMontant)}
                            </p>
                        </div>
                        
                        <Link
                            to="/demandes-reparation"
                            className="text-gray-700 hover:text-gray-900 text-sm font-medium"
                        >
                            ← Retour aux demandes
                        </Link>
                    </div>
   {/* Résumé */}
                        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="bg-white border border-gray-300 rounded p-4">
                                <div className="text-sm text-gray-500 mb-1">Total des réparations</div>
                                <div className="text-2xl font-bold text-gray-900">{filteredReparations.length}</div>
                            </div>
                            <div className="bg-white border border-gray-300 rounded p-4">
                                <div className="text-sm text-gray-500 mb-1">Montant total</div>
                                <div className="text-2xl font-bold text-gray-900">
                                    {formatCurrency(filteredReparations.reduce((sum, rep) => sum + parseFloat(rep.montant || 0), 0))}
                                </div>
                            </div>
                            <div className="bg-white border border-gray-300 rounded p-4">
                                <div className="text-sm text-gray-500 mb-1">Moyenne par réparation</div>
                                <div className="text-2xl font-bold text-gray-900">
                                    {formatCurrency(filteredReparations.length > 0 ? 
                                        filteredReparations.reduce((sum, rep) => sum + parseFloat(rep.montant || 0), 0) / filteredReparations.length : 0)}
                                </div>
                            </div>
                        </div>
                    {/* Barre de recherche */}
                    <div className="mb-8 mt-6">
                        <div className="relative">
                            <input
                                type="text"
                                placeholder="Rechercher par immatriculation, marque, modèle, nom..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full px-4 py-2.5 border border-gray-300 rounded focus:outline-none focus:border-gray-500 text-sm"
                            />
                            <svg 
                                className="absolute right-3 top-3 w-4 h-4 text-gray-400" 
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24" 
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </div>
                    </div>
                </div>

                {/* Tableau des réparations */}
                {filteredReparations.length === 0 ? (
                    <div className="bg-white border border-gray-300 rounded-lg p-8 text-center">
                        <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <h3 className="text-lg font-medium text-gray-600 mb-2">
                            {searchTerm ? 'Aucun résultat pour votre recherche' : 'Aucune réparation enregistrée'}
                        </h3>
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                className="text-gray-600 hover:text-gray-800 text-sm font-medium"
                            >
                                Effacer la recherche
                            </button>
                        )}
                    </div>
                ) : (
                    <>
                        <div className="bg-white border border-gray-300 rounded-lg overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="">
                                        <tr>
                                            <th className="text-left py-3 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                ID / Date
                                            </th>
                                            <th className="text-left py-3 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Véhicule
                                            </th>
                                            <th className="text-left py-3 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Mécanicien
                                            </th>
                                            <th className="text-left py-3 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Demandeur
                                            </th>
                                            <th className="text-left py-3 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Montant
                                            </th>
                                            <th className="text-left py-3 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Pièces
                                            </th>
                                            <th className="text-left py-3 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {filteredReparations.map((reparation) => (
                                            <tr key={reparation.id} className="hover:">
                                                {/* ID et Date */}
                                                <td className="py-3 px-6">
                                                    <div>
                                                        <div className="font-medium text-gray-900">#{reparation.id}</div>
                                                        <div className="text-xs text-gray-500 mt-1">
                                                            {formatDate(reparation.created_at)}
                                                        </div>
                                                    </div>
                                                </td>

                                                {/* Véhicule */}
                                                <td className="py-3 px-6">
                                                    {reparation.vehicule_info ? (
                                                        <div>
                                                            <div className="font-medium text-gray-900">
                                                                {reparation.vehicule_info.immatriculation}
                                                            </div>
                                                            
                                                        </div>
                                                    ) : (
                                                        <span className="text-gray-400 text-sm">Non disponible</span>
                                                    )}
                                                </td>

                                                {/* Mécanicien */}
                                                <td className="py-3 px-6">
                                                    <div className="flex items-center">
                                                        <div>
                                                            <div className="text-gray-900">
                                                                {reparation.mecanicien?.nom || 'Non spécifié'}
                                                            </div>
                                                            <div className="text-xs text-gray-500">
                                                               ( {reparation.mecanicien?.role || 'Mécanicien'})
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>

                                                {/* Demandeur */}
                                                <td className="py-3 px-6">
                                                    <div className="flex items-center">
                                                        <div>
                                                            <div className="text-gray-900">
                                                                {reparation.demandeur_info?.nom || 'Non spécifié'}
                                                            </div>
                                                            <div className="text-xs text-gray-500">
                                                                ({reparation.demandeur_info?.role || 'Client'})
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>

                                                {/* Montant */}
                                                <td className="py-3 px-6">
                                                    <div className="font-bold text-gray-900">
                                                        {formatCurrency(reparation.montant)}
                                                    </div>
                                                    
                                                </td>

                                                {/* Pièces */}
                                                <td className="py-3 px-6">
                                                    <div className="flex items-center">
                                                        <svg className="w-4 h-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                        </svg>
                                                        <span className="text-gray-700">
                                                            {reparation.pieces?.length || 0} pièce(s)
                                                        </span>
                                                    </div>
                                                </td>

                                                {/* Actions */}
                                                <td className="py-3 px-6">
                                                    <div className="flex space-x-2">
                                                        {/* Bouton Détails */}
                                                        <button
                                                            onClick={() => handleViewDetails(reparation.id)}
                                                            className="flex items-center text-gray-600 hover:text-gray-900 text-sm font-medium"
                                                            title="Voir les détails"
                                                        >
                                                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                            </svg>
                                                            Détails
                                                        </button>

                                                        {/* Bouton Ajouter pièce */}
                                                        <Link
                                                            to={`/pieces/nouvelle?reparation_id=${reparation.id}`}
                                                            className="flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium ml-3"
                                                            title="Ajouter une pièce"
                                                        >
                                                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                                            </svg>
                                                            Pièce
                                                        </Link>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                     
                    </>
                )}
            </div>
        </div>
    );
};

export default ListeReparationsVehicule;